//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as React from 'react'
import { ReactElement } from 'react'
import { TextField, Typography, Tooltip } from '@mui/material'
import { FieldMetaData } from '@shared/SchemaTypes'
import getDataValue from './getDataValue'
import { SchemaDescription, SchemaFieldDescription } from 'yup'

interface DisplayNameFieldProps {
  fieldSchema: SchemaFieldDescription
  initialValues?: Record<string, object> | object
  onChange?: (e: Event) => void
  register: any
  errors: any
  poolName?: string
}

export default function DisplayNameField(props: DisplayNameFieldProps): ReactElement {
  const { fieldSchema, initialValues, errors, register, poolName } = props
  const castErrors = errors as Record<string, Error>
  const schemaDescription = fieldSchema as SchemaDescription
  const fieldMetaData = schemaDescription.meta as FieldMetaData

  const propName: string = fieldMetaData.propName
  const defaultValue = getDataValue(initialValues as Record<string, object | string>, propName)
  const label = schemaDescription.label ?? ''

  // Use pool name as placeholder if available
  const placeholder = poolName || 'Optional display name'
  const tooltipTitle = poolName ? `Leave empty to use pool name: ${poolName}` : 'Optional display name for the vault'

  return (
    <React.Fragment>
      <Tooltip title={tooltipTitle} arrow>
        <TextField
          inputProps={{
            'data-testid': propName,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          defaultValue={defaultValue}
          placeholder={placeholder}
          id={propName}
          label={label}
          fullWidth
          margin="dense"
          {...register(propName)}
          error={castErrors[propName] != null}
        />
      </Tooltip>
      <Typography variant="inherit" color="textSecondary">
        {errors[propName]?.message}
      </Typography>
    </React.Fragment>
  )
}
